import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useRestoreFile, useSoftDeleteFile } from '@/api/files';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  file: { id: string; name: string; folder_id: string };
  onDeleted?: () => void;
}

export function DeleteConfirmModal({ open, onClose, file, onDeleted }: Props) {
  const del = useSoftDeleteFile();
  const restore = useRestoreFile();
  const toast = useToast();

  const handleConfirm = () => {
    del.mutate(
      { id: file.id, folder_id: file.folder_id },
      {
        onSuccess: () => {
          toast.success(`Deleted "${file.name}"`, {
            body: 'It has been moved to Trash.',
            durationMs: 8000,
            action: {
              label: 'Undo',
              onClick: async () => {
                try {
                  await restore.mutateAsync({ id: file.id, folder_id: file.folder_id });
                  toast.success('File restored');
                } catch (e) {
                  toast.error('Restore failed', apiError(e));
                }
              },
            },
          });
          onClose();
          onDeleted?.();
        },
        onError: (e) => toast.error('Delete failed', apiError(e)),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Delete file"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm} loading={del.isPending}>
            Delete
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-700">
        Are you sure you want to delete <span className="font-medium">{file.name}</span>? You can
        restore it from the Trash, and you'll have a few seconds to undo right after.
      </p>
    </Modal>
  );
}
